/* ══════════════════════════════════════════════════════════════════════
   PAG FILTER BAR — Hierarchical Scope Tree Filter (Group > Org > Dept > Div > App).

   Replaces the legacy flat dropdowns with the ScopeTreeFilter component
   matching Vulnerability Management. Backed by `/Filter/mainFilterApi`.

   Selections are held as a draft and applied on click of "Filter".
   On initial load, the first Application in the tree is automatically
   selected and applied, ensuring the graph and table open scoped to data.
   ══════════════════════════════════════════════════════════════════════ */
import { useEffect, useMemo, useRef, useState } from "react";
import { Filter as FilterIcon, TriangleAlert } from "lucide-react";
import { Button } from "../../../components/ui/button";
import { ScopeTreeFilter } from "../../itCompliance/components/ScopeTreeFilter";
import type { ScopeNode } from "../../itCompliance/types";
import {
  collectScopeIdsUnder,
  findFirstScopeIdByType,
  findScopeIdByAppCode,
  scopeTriggerLabel,
  selectedScopeApplicationCodes,
} from "../../itCompliance/utils";
import {
  fetchPagMainFilter,
  mapPagFilterToScopeTree,
} from "../services/pagApi";
import type { PagFilters } from "../data/pagTypes";

export function PagFilterBar({
  applied,
  onApply,
  onReady,
}: {
  applied: PagFilters;
  onApply: (next: PagFilters) => void;
  /** Fired once the filter tree has resolved and the default application
   *  has been applied — including when the call fails, so a bad filter
   *  response cannot leave the page waiting forever. */
  onReady?: () => void;
}) {
  const [tree, setTree] = useState<ScopeNode[]>([]);
  // null = user hasn't made uncommitted edits; string[] = user's active draft
  const [userEdits, setUserEdits] = useState<string[] | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const appliedRef = useRef(applied);
  appliedRef.current = applied;
  const onApplyRef = useRef(onApply);
  onApplyRef.current = onApply;
  const onReadyRef = useRef(onReady);
  onReadyRef.current = onReady;

  // Derive scope IDs directly from props + tree without any synchronization useEffect
  const appliedScopeIds = useMemo(() => {
    if (!tree.length || !applied.applications.length) return [];
    const ids: string[] = [];
    for (const code of applied.applications) {
      const id = findScopeIdByAppCode(tree, code);
      if (id) ids.push(...collectScopeIdsUnder(tree, id));
    }
    return Array.from(new Set(ids));
  }, [tree, applied.applications]);

  // Active selection is user's uncommitted draft, or derived applied selection
  const activeScope = userEdits ?? appliedScopeIds;

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);

    fetchPagMainFilter()
      .then((res) => {
        if (cancelled) return;
        const nodes = mapPagFilterToScopeTree(res);
        setTree(nodes);

        if (nodes.length > 0 && appliedRef.current.applications.length === 0) {
          // Default to first application if none was already applied
          const firstAppId = findFirstScopeIdByType(nodes, "application");
          if (firstAppId) {
            const initialIds = collectScopeIdsUnder(nodes, firstAppId);
            const initialAppCodes = selectedScopeApplicationCodes(nodes, initialIds);
            onApplyRef.current({
              departments: [],
              divisions: [],
              applications: initialAppCodes,
            });
          }
        }
      })
      .catch((e: unknown) => {
        if (cancelled) return;
        setTree([]);
        setError(e instanceof Error ? e.message : "Could not load the filters");
      })
      .finally(() => {
        if (cancelled) return;
        setLoading(false);
        onReadyRef.current?.();
      });

    return () => {
      cancelled = true;
    };
  }, []);

  const handleApply = () => {
    const appCodes = selectedScopeApplicationCodes(tree, activeScope);
    setUserEdits(null);
    onApply({
      departments: [],
      divisions: [],
      applications: appCodes,
    });
  };

  const handleClear = () => {
    setUserEdits(null);
    onApply({
      departments: [],
      divisions: [],
      applications: [],
    });
  };

  return (
    <div className="rounded-xl border border-border bg-card p-3">
      <div className="flex flex-wrap items-center gap-2">
        <ScopeTreeFilter
          data={tree}
          value={activeScope}
          onChange={setUserEdits}
          loading={loading}
          triggerLabel={scopeTriggerLabel(tree, activeScope, "Application")}
        />

        <Button
          variant="indigo"
          onClick={handleApply}
          disabled={activeScope.length === 0}
          className="justify-start h-10 gap-1.5 rounded-lg px-3.5 text-sm font-semibold disabled:opacity-60"
        >
          <FilterIcon className="size-3.5" />
          Filter
        </Button>

        <Button
          variant="outline"
          onClick={handleClear}
          disabled={activeScope.length === 0}
          className="justify-start h-10 px-4 text-sm font-medium text-foreground/70 hover:border-indigo-400/60 hover:text-foreground disabled:cursor-default disabled:opacity-40"
        >
          Clear
        </Button>
      </div>

      {error && (
        <div className="mt-2 flex items-start gap-1.5">
          <TriangleAlert className="mt-[1px] size-3.5 shrink-0 text-amber-600 dark:text-amber-400" />
          <span className="break-words text-[11px] leading-snug text-amber-800 dark:text-amber-200">
            {error}
          </span>
        </div>
      )}
    </div>
  );
}
